import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';
import React from 'react';

import type { RatingProps } from '../../Rating';
import type { FieldProps } from '../types';

import { ErrorMessage } from '../../ErrorMessage';
import { HelperMessage } from '../../HelperMessage';
import Iconify from '../../Iconify/Iconify';
import Rating from '../../Rating';
import './StarRating.scss';

function LevelIcon({
  className,
  filled,
  value,
}: {
  className?: string;
  filled: boolean;
  value: number;
}) {
  let iconName = `mdi:star${filled ? '' : '-outline'}`;

  if (typeof value === 'number' && value % 1 === 0.5) {
    iconName = 'mdi:star-half';
  }

  return <Iconify className={className} icon={iconName} size="large" />;
}

export type StarRatingProps = {
  disableClickOnly?: boolean;
} & Omit<FieldProps, 'defaultValue'> &
  RatingProps;

export const StarRating = React.forwardRef(
  (
    {
      disableClickOnly,
      errors,
      helperText,
      loading,
      onChange,
      value,
      ...props
    }: StarRatingProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const [ratingValue, setRatingValue] = React.useState<number>(value || 0);

    React.useEffect(() => {
      setRatingValue(value || 0);
    }, [value]);

    return (
      <>
        {loading ? (
          <Skeleton className={props.className} classes={props.classes} />
        ) : (
          <Rating
            IconContainerComponent={({ value }) => (
              <LevelIcon
                className="Mfw-StarRating-icon mr-2"
                filled={ratingValue >= value}
                value={value}
              />
            )}
            onChange={(e, newValue) => {
              if (disableClickOnly) return;
              setRatingValue(newValue ?? 0);
              if (onChange) onChange(e, newValue ?? 0);
            }}
            emptyIcon={
              <LevelIcon
                className="Mfw-StarRating-icon mr-2"
                filled={false}
                value={1}
              />
            }
            className={classNames('Mfw-StarRating-root', props.className)}
            size={props.size || 'medium'}
            {...props}
            precision={1}
            ref={ref}
          />
        )}
        {errors && <ErrorMessage errors={errors} />}
        {helperText && <HelperMessage>{helperText}</HelperMessage>}
      </>
    );
  },
);

StarRating.displayName = 'StarRating';

export default StarRating;
